import React, { useState, useEffect } from 'react';
import { KnowledgeBase, CreateKnowledgeBaseRequest, UpdateKnowledgeBaseRequest } from '@shared/types';
import { DIFFICULTY_LEVELS } from '@shared/constants';

interface KnowledgeBaseFormProps {
    knowledgeBase?: KnowledgeBase;
    onSubmit: (data: CreateKnowledgeBaseRequest | UpdateKnowledgeBaseRequest) => void;
    onCancel: () => void;
    loading?: boolean;
}

export const KnowledgeBaseForm: React.FC<KnowledgeBaseFormProps> = ({
    knowledgeBase,
    onSubmit,
    onCancel,
    loading = false
}) => {
    const [formData, setFormData] = useState<CreateKnowledgeBaseRequest>({
        name: '',
        description: '',
        category: '',
        subject: '',
        difficulty_level: 'beginner',
        target_audience: ''
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (knowledgeBase) {
            setFormData({
                name: knowledgeBase.name,
                description: knowledgeBase.description || '',
                category: knowledgeBase.category,
                subject: knowledgeBase.subject,
                difficulty_level: knowledgeBase.difficulty_level,
                target_audience: knowledgeBase.target_audience || ''
            });
        }
    }, [knowledgeBase]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = '知识库名称不能为空';
        }

        if (!formData.category.trim()) {
            newErrors.category = '请选择分类';
        }

        if (!formData.subject.trim()) {
            newErrors.subject = '请选择科目';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm()) {
            onSubmit(formData);
        }
    };

    const handleInputChange = (field: keyof CreateKnowledgeBaseRequest, value: string) => {
        setFormData((prev: CreateKnowledgeBaseRequest) => ({
            ...prev,
            [field]: value
        }));

        // 清除字段错误
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const getDifficultyDisplayName = (difficulty: string) => {
        const difficultyMap: Record<string, string> = {
            [DIFFICULTY_LEVELS.BEGINNER]: '初级',
            [DIFFICULTY_LEVELS.INTERMEDIATE]: '中级',
            [DIFFICULTY_LEVELS.ADVANCED]: '高级',
        };
        return difficultyMap[difficulty] || difficulty;
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                    {knowledgeBase ? '编辑知识库' : '创建知识库'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                    {knowledgeBase ? '修改知识库信息' : '填写知识库基本信息'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* 知识库名称 */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        知识库名称 <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-300' : 'border-gray-300'
                            }`}
                        placeholder="请输入知识库名称"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                    {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                </div>

                {/* 描述 */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        描述
                    </label>
                    <textarea
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="请输入知识库描述（可选）"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                    />
                </div>

                {/* 分类和科目 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            分类 <span className="text-red-500">*</span>
                        </label>
                        <select
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.category ? 'border-red-300' : 'border-gray-300'
                                }`}
                            value={formData.category}
                            onChange={(e) => handleInputChange('category', e.target.value)}
                        >
                            <option value="">请选择分类</option>
                            <option value="数学">数学</option>
                            <option value="计算机科学">计算机科学</option>
                            <option value="物理">物理</option>
                            <option value="化学">化学</option>
                            <option value="生物">生物</option>
                            <option value="历史">历史</option>
                            <option value="地理">地理</option>
                            <option value="文学">文学</option>
                            <option value="艺术">艺术</option>
                        </select>
                        {errors.category && (
                            <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            科目 <span className="text-red-500">*</span>
                        </label>
                        <select
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.subject ? 'border-red-300' : 'border-gray-300'
                                }`}
                            value={formData.subject}
                            onChange={(e) => handleInputChange('subject', e.target.value)}
                        >
                            <option value="">请选择科目</option>
                            <option value="基础数学">基础数学</option>
                            <option value="高等数学">高等数学</option>
                            <option value="线性代数">线性代数</option>
                            <option value="概率统计">概率统计</option>
                            <option value="编程">编程</option>
                            <option value="算法">算法</option>
                            <option value="数据结构">数据结构</option>
                            <option value="机器学习">机器学习</option>
                            <option value="深度学习">深度学习</option>
                        </select>
                        {errors.subject && (
                            <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                        )}
                    </div>
                </div>

                {/* 难度级别和目标用户 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            难度级别 <span className="text-red-500">*</span>
                        </label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={formData.difficulty_level}
                            onChange={(e) => handleInputChange('difficulty_level', e.target.value)}
                        >
                            <option value="beginner">初级</option>
                            <option value="intermediate">中级</option>
                            <option value="advanced">高级</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                            {getDifficultyDisplayName(formData.difficulty_level)} -
                            {formData.difficulty_level === 'beginner' && '适合初学者，基础概念和简单应用'}
                            {formData.difficulty_level === 'intermediate' && '适合有一定基础的学习者，包含进阶概念'}
                            {formData.difficulty_level === 'advanced' && '适合高级学习者，包含复杂概念和高级应用'}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            目标用户
                        </label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="例如：小学生、中学生、大学生、职场人士"
                            value={formData.target_audience}
                            onChange={(e) => handleInputChange('target_audience', e.target.value)}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            描述该知识库适合哪些用户群体
                        </p>
                    </div>
                </div>

                {/* 操作按钮 */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={loading}
                    >
                        取消
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        {loading ? (
                            <div className="flex items-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                保存中...
                            </div>
                        ) : (
                            knowledgeBase ? '更新' : '创建'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};
